import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Flex, Image, HStack, Collapse, VStack, useDisclosure } from "@chakra-ui/react";
import { Link as ScrollLink } from "react-scroll";
import Hamburger from "./Hamburger";
import blackLogo from "../assets/logo.svg";
import mogomaLogo from "../assets/mogoma.png";

const Header = ({ headerBg = "#000000", forceMogoma = false }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [active, setActive] = useState("Home");
  const location = useLocation();
  const navigate = useNavigate();

  const isMogomaPage = location.pathname === "/mogoma";

  const mainLinks = [
    { to: "home", text: "Home" },
    { to: "about", text: "About" },
    { to: "jobs", text: "Jobs" },
    { to: "contact-us", text: "Contact Us" },
  ];

  const mogomaLinks = [
    { to: "hero", text: "Home" },
    { to: "features", text: "Features" },
    { to: "shop", text: "Shop" },
    { to: "delivery-api", text: "Delivery API" },
    { to: "how-it-works", text: "How It Works" },
    { to: "reviews", text: "Reviews" },
  ];

  const linksToRender = forceMogoma || isMogomaPage ? mogomaLinks : mainLinks;

  const handleLogoClick = () => {
    if (isMogomaPage || forceMogoma) navigate("/mogoma#hero");
    else navigate("/");
  };

  const handleLinkClick = (link) => {
    if (isMogomaPage || forceMogoma) {
      setActive(link.text);
      return;
    }
    if (location.pathname === "/") {
      const el = document.getElementById(link.to);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        setActive(link.text);
      }
    } else {
      navigate(`/#${link.to}`);
    }
  };

  useEffect(() => {
    if (isMogomaPage && location.hash) {
      const sectionId = location.hash.replace("#", "");
      const el = document.getElementById(sectionId);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 200);
    }
  }, [location, isMogomaPage]);

  const handleMobileNavToggle = () => (isOpen ? onClose() : onOpen());

  const mobileTextColor = isMogomaPage || forceMogoma
    ? { active: "#1454F3", inactive: "#000000" }
    : { active: "#FFFFFF", inactive: "#FFFFFF" };

  const navBg = forceMogoma || isMogomaPage ? "white" : headerBg;
  const linkColor = forceMogoma || isMogomaPage ? "#00000080" : "white";

  useEffect(() => {
    if (!isMogomaPage) {
      const handleScroll = () => {
        const scrollPos = window.scrollY + 70;
        let current = "Home";
        mainLinks.forEach(link => {
          const el = document.getElementById(link.to);
          if (el && el.offsetTop <= scrollPos) {
            current = link.text;
          }
        });
        setActive(current);
      };

      window.addEventListener("scroll", handleScroll);
      handleScroll();
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [isMogomaPage]);


  const renderDesktopLink = (link) => {
    if (isMogomaPage) {
      // Mogoma blue underline
      return (
        <ScrollLink
          to={link.to}
          smooth
          duration={500}
          spy
          offset={-60}
          onSetActive={() => setActive(link.text)}
          style={{ position: "relative", height: "60px", lineHeight: "60px" }}
        >
          <Box
            _hover={{ textDecoration: "none", color: "#00000080" }}
            color={active === link.text ? "#1454F3" : "inherit"}
            _after={{
              content: '""',
              position: "absolute",
              bottom: "0",
              left: "0",
              width: active === link.text ? "100%" : "0",
              height: "4px",
              bg: "#1454F3",
              transition: "width 0.3s",
              borderTopLeftRadius: "14px",
              borderTopRightRadius: "14px",
            }}
          >
            {link.text}
          </Box>
        </ScrollLink>
      );
    } else {
      // Non-Mogoma white underline (same style as Mogoma)
      return (
        <Box
          onClick={() => handleLinkClick(link)}
          position="relative"
          _after={{
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            width: active === link.text ? "100%" : "0",
            height: "4px",
            bg: "white",
            transition: "width 0.3s",
            borderTopLeftRadius: "14px",
            borderTopRightRadius: "14px",
          }}
          _hover={{
            _after: {
              width: "100%",
            },
          }}
          px={1}
        >
          {link.text}
        </Box>
      );
    }
  };

  return (
    <Box
      as="nav"
      bg={navBg}
      backdropFilter="blur(10px)"
      borderRadius={!isOpen ? "20px" : "none"}
      px={8}
      mt={isOpen ? 0 : 4}
      mx={{ base: isOpen ? 0 : 4, md: "auto" }}
      zIndex={99999}
      maxW="1200px"
      height={isOpen ? "100vh" : "60px"}
      position="fixed"
      top={0}
      left={0}
      right={0}
      transition="all 0.3s ease-in-out"
    >
      <Flex alignItems={isOpen ? "start" : "center"} justifyContent="center" height="100%">
        {/* Mobile Logo */}
        <Box display={{ base: "flex", md: "none" }} my={6} mr="auto">
          <Box as="button" onClick={handleLogoClick}>
            <Image
              src={forceMogoma || isMogomaPage ? mogomaLogo : blackLogo}
              w={forceMogoma || isMogomaPage ? { base: "120px", sm: "140px" } : { base: "140px", md: "160px" }}
              h="auto"
              objectFit="contain"
            />
          </Box>
        </Box>

        {/* Mobile Hamburger */}
        <Box display={{ base: "flex", md: "none" }} m={4}>
          <Hamburger
            isOpened={isOpen}
            handleMobileNavToggle={handleMobileNavToggle}
            isMogomaPage={forceMogoma || isMogomaPage}
          />
        </Box>

        {/* Desktop Left Links */}
        <HStack
          spacing={10}
          color={linkColor}
          height="100%"
          alignItems="center"
          display={{ base: "none", md: "flex" }}
          justify="center"
          flex="1"
        >
          {linksToRender.slice(0, 3).map((link, idx) => (
            <Box key={idx} cursor="pointer">
              {renderDesktopLink(link)}
            </Box>
          ))}
        </HStack>

        {/* Desktop Logo */}
        <Box mx={{ base: 0, md: 4 }} display={{ base: "none", md: "flex" }} justifyContent="center">
          <Box as="button" onClick={handleLogoClick}>
            <Image src={forceMogoma || isMogomaPage ? mogomaLogo : blackLogo} w={52} h={10} alt="Logo" />
          </Box>
        </Box>

        {/* Desktop Right Links */}
        <HStack
          spacing={10}
          color={linkColor}
          height="100%"
          alignItems="center"
          display={{ base: "none", md: "flex" }}
          justify="center"
          flex="1"
        >
          {linksToRender.slice(3).map((link, idx) => (
            <Box key={idx} cursor="pointer">
              {renderDesktopLink(link)}
            </Box>
          ))}
        </HStack>
      </Flex>

      {/* Mobile Navigation Links */}
      <Collapse in={isOpen} animateOpacity>
        <VStack pos="absolute" top="60px" left={0} right={0} zIndex={9999} py={4} bg="transparent">
          {linksToRender.map((link, idx) => (
            <Box key={idx} width="100%">
              {isMogomaPage ? (
                <Box width="100%" textAlign="center" py="10px" fontWeight="600" fontSize="14px" color={active === link.text ? "#1454F3" : "#000000"}>
                  <ScrollLink
                    to={link.to}
                    smooth
                    duration={500}
                    spy
                    offset={-60}
                    onClick={() => { setActive(link.text); handleMobileNavToggle(); }}
                    style={{ display: "block" }}
                  >
                    {link.text}
                  </ScrollLink>
                </Box>
              ) : (
                <Box
                  onClick={() => { handleLinkClick(link); handleMobileNavToggle(); }}
                  width="100%"
                  textAlign="center"
                  py="10px"
                  fontWeight="600"
                  fontSize="14px"
                  color={active === link.text ? mobileTextColor.active : mobileTextColor.inactive}
                  cursor="pointer"
                >
                  {link.text}
                </Box>
              )}
            </Box>
          ))}
        </VStack>
      </Collapse>
    </Box>
  );
};

export default Header;
